import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  Alert,
  Pagination,
  InputAdornment,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  Edit as EditIcon,
  Cancel as CancelIcon,
  Extension as ExtensionIcon,
  Analytics as AnalyticsIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  PersonAdd as AssignIcon,
  Groups as BulkIcon
} from '@mui/icons-material';
import { subscriptionService } from '../../services/subscriptionService';
import  planService  from '../../services/planService';

const SubscriptionManagement = () => {
  // State management
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Pagination and filtering
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Dialog states
  const [assignDialog, setAssignDialog] = useState(false);
  const [extendDialog, setExtendDialog] = useState(false);
  const [bulkDialog, setBulkDialog] = useState(false);
  const [analyticsDialog, setAnalyticsDialog] = useState(false);
  
  // Selected items
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  
  // Form data
  const [assignForm, setAssignForm] = useState({
    userId: '',
    planId: '',
    durationOverride: ''
  });
  const [extendForm, setExtendForm] = useState({
    additionalDays: '',
    reason: ''
  });
  const [bulkForm, setBulkForm] = useState({
    action: '',
    planId: '',
    additionalDays: ''
  });

  // Fetch data
  useEffect(() => {
    fetchData();
    fetchPlans();
  }, [page, searchTerm, statusFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await subscriptionService.getSubscriptions({
        page,
        search: searchTerm,
        status: statusFilter
      });
      setUsers(response.users || []);
      setTotalPages(response.totalPages || 1);
    } catch (err) {
      setError('Failed to fetch subscription data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await planService.getPlans();
      setPlans(response.plans || []);
    } catch (err) {
      console.error('Failed to fetch plans:', err);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await subscriptionService.getAnalytics();
      setAnalytics(response.analytics);
      setAnalyticsDialog(true);
    } catch (err) {
      setError('Failed to fetch analytics');
    }
  };

  // Subscription management actions
  const handleAssignPlan = async () => {
    try {
      await subscriptionService.assignPlan(assignForm);
      setSuccess('Plan assigned successfully');
      setAssignDialog(false);
      setAssignForm({ userId: '', planId: '', durationOverride: '' });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to assign plan');
    }
  };

  const handleCancelSubscription = async (userId, reason = 'Admin cancellation') => {
    if (!window.confirm('Are you sure you want to cancel this subscription?')) return;
    
    try {
      await subscriptionService.cancelSubscription(userId, reason);
      setSuccess('Subscription cancelled successfully');
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to cancel subscription');
    }
  };

  const handleExtendSubscription = async () => {
    try {
      await subscriptionService.extendSubscription(selectedUser.id, extendForm);
      setSuccess('Subscription extended successfully');
      setExtendDialog(false);
      setExtendForm({ additionalDays: '', reason: '' });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to extend subscription');
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedUsers.length === 0) {
      setError('Please select users for bulk update');
      return;
    }

    try {
      await subscriptionService.bulkUpdate({
        userIds: selectedUsers,
        ...bulkForm
      });
      setSuccess(`Bulk ${bulkForm.action} completed successfully`);
      setBulkDialog(false);
      setBulkForm({ action: '', planId: '', additionalDays: '' });
      setSelectedUsers([]);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Bulk update failed');
    }
  };

  // Helper functions
  const getStatusColor = (status) => {
    const colors = {
      'active': 'success',
      'expired': 'error',
      'cancelled': 'warning',
      'pending': 'info'
    };
    return colors[status] || 'default';
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString();
  };

  const isUserSelected = (userId) => selectedUsers.includes(userId);

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(user => user._id));
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Subscription Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<AssignIcon />}
            onClick={() => setAssignDialog(true)}
          >
            Assign Plan
          </Button>
          <Button
            variant="outlined"
            startIcon={<BulkIcon />}
            onClick={() => setBulkDialog(true)}
            disabled={selectedUsers.length === 0}
          >
            Bulk Actions ({selectedUsers.length})
          </Button>
          <Button
            variant="outlined"
            startIcon={<AnalyticsIcon />}
            onClick={fetchAnalytics}
          >
            Analytics
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status Filter</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status Filter"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="expired">Expired</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchData}
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Subscriptions Table */}
      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Checkbox
                      checked={selectedUsers.length === users.length && users.length > 0}
                      indeterminate={selectedUsers.length > 0 && selectedUsers.length < users.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Current Plan</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell>Days Remaining</TableCell>
                  <TableCell>Times Purchased</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={user._id}>
                      <TableCell>
                        <Checkbox
                          checked={isUserSelected(user._id)}
                          onChange={() => handleSelectUser(user._id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {user.name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {user.email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {user.active_subscription?.plan_id?.name || 'No Plan'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.active_subscription?.status || 'None'}
                          color={getStatusColor(user.active_subscription?.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {formatDate(user.active_subscription?.start_date)}
                      </TableCell>
                      <TableCell>
                        {formatDate(user.active_subscription?.end_date)}
                      </TableCell>
                      <TableCell>
                        {user.active_subscription?.daysRemaining || 0} days
                      </TableCell>
                      <TableCell>
                        {user.purchase_count || 0}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Extend Subscription">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedUser(user);
                                setExtendDialog(true);
                              }}
                              disabled={!user.active_subscription}
                            >
                              <ExtensionIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancel Subscription">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleCancelSubscription(user._id)}
                              disabled={user.active_subscription?.status !== 'active'}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Assign Plan Dialog */}
      <Dialog open={assignDialog} onClose={() => setAssignDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Plan to User</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="User ID"
            value={assignForm.userId}
            onChange={(e) => setAssignForm({...assignForm, userId: e.target.value})}
            margin="normal"
            helperText="Enter the user ID to assign a plan"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Select Plan</InputLabel>
            <Select
              value={assignForm.planId}
              label="Select Plan"
              onChange={(e) => setAssignForm({...assignForm, planId: e.target.value})}
            >
              {plans.map((plan) => (
                <MenuItem key={plan._id} value={plan._id}>
                  {plan.name} - {plan.duration_days} days
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Duration Override (days)"
            type="number"
            value={assignForm.durationOverride}
            onChange={(e) => setAssignForm({...assignForm, durationOverride: e.target.value})}
            margin="normal"
            helperText="Leave empty to use plan's default duration"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialog(false)}>Cancel</Button>
          <Button onClick={handleAssignPlan} variant="contained">
            Assign Plan
          </Button>
        </DialogActions>
      </Dialog>

      {/* Extend Subscription Dialog */}
      <Dialog open={extendDialog} onClose={() => setExtendDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Extend Subscription</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Extending subscription for: {selectedUser?.name}
          </Typography>
          <TextField
            fullWidth
            label="Additional Days"
            type="number"
            value={extendForm.additionalDays}
            onChange={(e) => setExtendForm({...extendForm, additionalDays: e.target.value})}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Reason"
            multiline
            rows={3}
            value={extendForm.reason}
            onChange={(e) => setExtendForm({...extendForm, reason: e.target.value})}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtendDialog(false)}>Cancel</Button>
          <Button onClick={handleExtendSubscription} variant="contained">
            Extend Subscription
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Actions Dialog */}
      <Dialog open={bulkDialog} onClose={() => setBulkDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Actions ({selectedUsers.length} users selected)</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Action</InputLabel>
            <Select
              value={bulkForm.action}
              label="Action"
              onChange={(e) => setBulkForm({...bulkForm, action: e.target.value})}
            >
              <MenuItem value="cancel">Cancel Subscriptions</MenuItem>
              <MenuItem value="extend">Extend Subscriptions</MenuItem>
              <MenuItem value="change_plan">Change Plan</MenuItem>
            </Select>
          </FormControl>

          {bulkForm.action === 'extend' && (
            <TextField
              fullWidth
              label="Additional Days"
              type="number"
              value={bulkForm.additionalDays}
              onChange={(e) => setBulkForm({...bulkForm, additionalDays: e.target.value})}
              margin="normal"
              required
            />
          )}

          {bulkForm.action === 'change_plan' && (
            <FormControl fullWidth margin="normal">
              <InputLabel>New Plan</InputLabel>
              <Select
                value={bulkForm.planId}
                label="New Plan"
                onChange={(e) => setBulkForm({...bulkForm, planId: e.target.value})}
              >
                {plans.map((plan) => (
                  <MenuItem key={plan._id} value={plan._id}>
                    {plan.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDialog(false)}>Cancel</Button>
          <Button onClick={handleBulkUpdate} variant="contained">
            Apply Bulk Action
          </Button>
        </DialogActions>
      </Dialog>

      {/* Analytics Dialog */}
      <Dialog open={analyticsDialog} onClose={() => setAnalyticsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Subscription Analytics</DialogTitle>
        <DialogContent>
          {analytics && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Status Distribution</Typography>
                {analytics.statusDistribution.map((item) => (
                  <Box key={item._id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>{item._id || 'No Status'}</Typography>
                    <Chip label={item.count} size="small" />
                  </Box>
                ))}
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Plan Popularity</Typography>
                {analytics.planPopularity.map((item) => (
                  <Box key={item._id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>{item.planName}</Typography>
                    <Chip label={item.count} size="small" />
                  </Box>
                ))}
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>Expiring Soon ({analytics.expiringSubscriptions.length})</Typography>
                {analytics.expiringSubscriptions.map((user) => (
                  <Box key={user.userId} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>{user.name} ({user.planName})</Typography>
                    <Chip label={`${user.daysRemaining} days`} color="warning" size="small" />
                  </Box>
                ))}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnalyticsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubscriptionManagement;
